import { useEffect, useState } from "react";
import { Usb, Loader2, Zap, Check, X } from "lucide-react";
import { toast } from "sonner";
import { webSerialSupported } from "../../lib/transport";
import { SERIAL_CHIP_VIDS, VIDS } from "../../engine/detect";

const hex = (n) => (n == null ? "" : n.toString(16).toUpperCase().padStart(4, "0"));
export const portLabel = (info) => {
  const vid = hex(info?.usbVendorId);
  const brand = VIDS[vid] || (SERIAL_CHIP_VIDS[vid] ? `${SERIAL_CHIP_VIDS[vid]} USB-serial cutter` : "");
  return brand ? `${brand} (USB ${vid}:${hex(info?.usbProductId)})` : vid ? `USB plotter ${vid}:${hex(info?.usbProductId)}` : "Serial plotter";
};

// One-click direct USB connection from Chrome / Edge: the browser's own port picker is the whole set-up — nothing to install.
// Works for every cutter Windows shows as a COM port (Titan, Vevor, SC2, Graphtec/Roland via USB-serial, CH340/FTDI clones).
export const QuickConnect = ({ c, saveConnection, machines, onMachine }) => {
  const [busy, setBusy] = useState(false);
  const [granted, setGranted] = useState([]);
  const supported = webSerialSupported();
  const direct = c.transport === "webserial";
  useEffect(() => { if (supported) navigator.serial.getPorts().then((ps) => setGranted(ps.map((p) => portLabel(p.getInfo())))).catch(() => {}); }, [supported, direct]);
  const choose = async () => {
    setBusy(true);
    try {
      const port = await navigator.serial.requestPort();
      const info = port.getInfo();
      const label = portLabel(info);
      saveConnection({ ...c, transport: "webserial", baud: Number(c.baud) || 9600, flow: "none", directLabel: label, pinned: true, userPicked: true });
      const brand = VIDS[hex(info.usbVendorId)];
      const m = brand ? machines.find((x) => x.brand === brand) : machines.find((x) => x.brand === "Generic" && x.command_format === "hpgl");
      if (m) onMachine?.(m, Boolean(brand));
      setGranted([label]);
      toast.success(`${label} connected — cut the 2" test square to confirm`);
    } catch (e) {
      if (e?.name !== "NotFoundError") toast.error(e.message || "Could not open the plotter");
    } finally { setBusy(false); }
  };
  const forget = async () => {
    try { for (const p of await navigator.serial.getPorts()) await p.forget?.(); } catch { /* older Chrome: no forget() */ }
    setGranted([]);
    saveConnection({ ...c, transport: "download", directLabel: "" });
  };
  return (
    <div className={`rounded-md border ${direct ? "border-[#3ecf8e]/50 bg-[#3ecf8e]/5" : "border-[#27324a] bg-[#0c1017]"} px-3 py-2.5`} data-testid="quick-connect">
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${direct ? "bg-[#3ecf8e]/20 text-[#3ecf8e]" : "bg-[#2f80ff]/15 text-[#4c9ffe]"}`}>{direct ? <Check className="h-4 w-4" /> : <Zap className="h-4 w-4" />}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold" data-testid="quick-connect-title">{direct ? `Connected directly · ${c.directLabel || granted[0] || "USB plotter"}` : "Quick connect — plug the plotter into THIS computer"}</div>
          <div className="text-[11px] text-[#9aa0a8]">{direct ? "This browser talks to the plotter over USB. Nothing else to install." : supported ? "No download, no helper: pick the plotter in the browser's USB list and cut. Chrome or Edge, Windows or Mac." : "Needs Chrome or Edge on a desktop computer — this browser cannot talk to USB."}</div>
        </div>
        {direct ? (
          <button data-testid="quick-connect-forget" className="btn" onClick={forget} title="Disconnect"><X className="h-3.5 w-3.5" /></button>
        ) : (
          <button data-testid="quick-connect-btn" className="btn-red whitespace-nowrap" disabled={!supported || busy} onClick={choose}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Usb className="h-3.5 w-3.5" />} Choose plotter…</button>
        )}
      </div>
      {!direct && supported && <div className="mt-1.5 text-[10px] text-[#6b7079]">Shows up as a COM port? (Titan, Vevor, SC2, Graphtec / Roland on USB-serial) → this works. MH series (USB printer) → use the shop-computer helper below.</div>}
    </div>
  );
};
